/**
 * Represents a Form Element e.g. input, textarea, select
 * all elements templates and stylings are defined by FSK
 */
class FormElement {
  /**
   * @param {HTMLElement} element - form element
   */
  constructor(element) {
    this.element = element;

    this.config = {
      classes: {
        touched: "is-touched",
      },
      attributes: {
        customError: "data-custom-error",
        patternError: "data-webform-pattern-error",
        requiredError: "data-webform-required-error",
        zipId: "data-zip-id",
        locationInputId: "edit-address-city",
      },
      inputRules: {
        DE: {
          zip: {
            pattern: "^[\\d]{5}$",
            maxLength: 5,
            inputMode: "numeric",
          },
          location: {
            pattern: "^[^\\d]{2,100}$", // Pattern that disallows numbers
          },
        },
        default: {
          zip: {
            pattern: "^[\\w\\d\\-]{1,6}$",
            maxLength: 6,
            inputMode: "text",
          },
          location: {
            pattern: "^.{2,100}$",
          },
        },
      },
    };

    this.customError = this.element.getAttribute(
      this.config.attributes.customError
    );
    this.patternError = this.element.getAttribute(
      this.config.attributes.patternError
    );
    this.requiredError = this.element.getAttribute(
      this.config.attributes.requiredError
    );

    this.isCountry = this.element.hasAttribute(this.config.attributes.zipId);

    this.validate = this.validate.bind(this);
    this._updateAttributes = this._updateAttributes.bind(this);

    this._init();
  }

  /**
   * init
   *
   * @private
   */
  _init() {
    this._setErrorContainer();
    this._bind();
  }

  /**
   * sets correct error container of the form element
   * e.g. FskRadio lives in FskRadios which is a Fieldset, FskFieldset has a different error containers then FskFormElement
   *
   * @private
   */
  _setErrorContainer() {
    const parentMap = {
      FskOption: ".FskFieldset",
      Default: ".FskFormElement",
    };

    // check if element is placed inside the "FskOptions" (valid for checkboxes/radiobuttons) and define proper parent
    const parentSelector = this.element.closest(".FskOptions")
      ? parentMap.FskOption
      : parentMap.Default;
    const parent = this.element.closest(parentSelector);

    // set error container based on parent
    this.errorContainer = parent
      ? parent.querySelector(`${parentSelector}-errorMessage`)
      : null;
  }

  /**
   * binds events
   *
   * @private
   */
  _bind() {
    this.element.addEventListener("input", this.validate);

    if (this.isCountry) {
      this._updateAttributes();
      this.element.addEventListener("change", this._updateAttributes);
    }
  }

  /**
   * updates the zip code and the location patterns based on the selected country
   * German Location can't contain numbers
   */
  _updateAttributes() {
    const hasRule = Object.keys(this.config.inputRules).some(
      (key) => this.element.value === key
    );
    const country = hasRule ? this.element.value : "default";
    const zipCodeId = this.element.getAttribute(this.config.attributes.zipId);
    const zipCodeInput = document.querySelector(`#${zipCodeId}`);
    const locationInput = document.querySelector(
      `#${this.config.attributes.locationInputId}`
    );

    if (zipCodeInput && this.config.inputRules[country].zip) {
      const { pattern, maxLength, inputMode } =
        this.config.inputRules[country].zip;
      zipCodeInput.setAttribute("pattern", pattern);
      zipCodeInput.setAttribute("maxlength", maxLength);
      zipCodeInput.setAttribute("inputmode", inputMode);
    }

    if (locationInput && this.config.inputRules[country].location) {
      const { pattern } = this.config.inputRules[country].location;
      locationInput.setAttribute("pattern", pattern);
    }
  }

  /**
   * validates user input and sets error message
   */
  validate() {
    if (!this.element.classList.contains(this.config.classes.touched)) {
      this.element.classList.add(this.config.classes.touched);
    }

    if (this.errorContainer) {
      this.errorContainer.innerHTML = !this.element.validity.valid
        ? this.getErrorMessage()
        : "";
    }
  }

  /**
   * Set up appropriate error message(s).
   * Concatenate all of them if there is more than one.
   *
   * @returns {string} errorMessage - error message
   */
  getErrorMessage() {
    const potentialErrors = [
      this.patternError,
      this.requiredError,
      this.customError,
    ];
    const errors = potentialErrors.filter((error) => error);
    return errors.length > 0 ? errors.join("<br/>") : "Fehlerhafte Eingabe";
  }

  /**
   * returns if the element is valid based on the ValidityState
   *
   * @returns {boolean} valid - validity state
   */
  isValid() {
    return this.element.validity.valid;
  }

  /**
   * sets the focus to the element
   */
  focus() {
    this.element.focus();
  }
}

export default FormElement;
